import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import styled from '@emotion/styled';
import { ArrowLeft, AlertTriangle, Lightbulb, ChevronDown, ChevronUp, Loader2, FileText, Sparkles, Users, RefreshCw } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import PivotClusteredListView from './PivotClusteredListView';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { lensDisplayName } from '../../utils/lensDisplay';
import AIButton from '../AIButton';

const COLORS = {
  obstacle: '#E94B6E',
  opportunity: '#A6D64D',
};

// ─── Layout ─────────────────────────────────────────────────────────

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  color: white;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 32px 16px;
  flex-shrink: 0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  cursor: pointer;
  flex-shrink: 0;
  margin-top: 2px;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const PivotTitle = styled.h1`
  margin: 0 0 4px 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
`;

const PivotDescription = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.4;
`;

// ─── Summary Bar ────────────────────────────────────────────────────

const SummaryBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 32px 16px;
  flex-shrink: 0;
`;

const SummaryBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  background: ${p => p.$color}15;
  color: ${p => p.$color};
  border: 1px solid ${p => p.$color}30;
`;

// ─── Scrollable Content ─────────────────────────────────────────────

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 32px 40px;
`;

const ContentGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 16px;
  align-items: start;
`;

const LeftColumn = styled.div`
  min-width: 0;
`;

const RightSidebar = styled.div`
  position: sticky;
  top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MetaSection = styled.div`
  max-width: 720px;
  margin: 0 auto 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryPanel = styled.div`
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  padding: 14px 16px;
`;

const SummaryTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 0.86rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.65);
`;

const SummaryParagraph = styled.p`
  margin: 0 0 10px 0;
  font-size: 0.86rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.86);

  &:last-child {
    margin-bottom: 0;
  }
`;

const LensCountsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const LensCountCard = styled.div`
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  padding: 10px 12px;
`;

const LensCountTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  margin-bottom: 6px;
`;

const LensCountRow = styled.div`
  display: flex;
  gap: 10px;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
`;

const StatusMessage = styled.div`
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  padding: 12px 14px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.84rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ReportContent = styled.div`
  font-size: 0.8rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.85);
  max-height: 52vh;
  overflow-y: auto;
  padding-right: 4px;

  h4 {
    color: white;
    margin: 10px 0 6px 0;
    font-size: 0.85rem;
  }

  p {
    margin: 0 0 10px 0;
  }
`;

const ReportButton = styled(AIButton)`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.8rem;
`;
ReportButton.defaultProps = { $variant: 'solid' };

const CardsContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ViewToggleRow = styled.div`
  max-width: 720px;
  margin: 0 auto 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const ToggleBtn = styled.button`
  border: 1px solid ${(p) => (p.$active ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)')};
  background: ${(p) => (p.$active ? 'rgba(255,255,255,0.12)' : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : 'rgba(255,255,255,0.6)')};
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.8rem;
  cursor: pointer;
`;

const StaleClusterHint = styled.div`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.45);
  font-style: italic;
  margin-left: auto;
  max-width: 280px;
  text-align: right;
  line-height: 1.4;
`;

// ─── Lens Card ──────────────────────────────────────────────────────

const LensCardWrapper = styled.div`
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 4px solid ${p => p.$color || '#367ABA'};
  background: rgba(255, 255, 255, 0.03);
  overflow: hidden;
`;

const LensCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
`;

const LensImage = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p => p.$imageUrl
    ? `url(${p.$imageUrl}) center/cover no-repeat`
    : `linear-gradient(135deg, ${p.$color}55, ${p.$color}22)`
  };
  border: 2px solid ${p => p.$color}44;

  html[data-platform='tizen'] & {
    background: ${p => p.$imageUrl
      ? `url(${p.$imageUrl}) center/cover no-repeat`
      : `${p.$color}33`
    };
  }
`;

const LensName = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  flex: 1;
`;

const LensCountBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: ${p => p.$color};
  opacity: 0.85;
`;

const LensCardBody = styled.div`
  padding: 0 18px 16px;
`;

const EmptyLens = styled.div`
  padding: 12px 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.2);
  font-style: italic;
`;

// ─── Type Section (obstacles / opportunities) ───────────────────────

const TypeSection = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TypeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${p => p.$color};
  margin-bottom: 6px;
`;

const BulletList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

// ─── Bullet Item (expandable) ───────────────────────────────────────

const BulletItem = styled.li`
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);

  &:last-child {
    border-bottom: none;
  }
`;

const BulletRow = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
  line-height: 1.4;
  text-align: left;
  cursor: ${p => p.$expandable ? 'pointer' : 'default'};

  &:hover {
    color: ${p => p.$expandable ? '#fff' : 'rgba(255, 255, 255, 0.85)'};
  }
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${p => p.$color};
  flex-shrink: 0;
  margin-top: 7px;
`;

const BulletTitle = styled.span`
  flex: 1;
`;

const ExpandIcon = styled.span`
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.3);
  margin-top: 2px;
`;

const BulletDescription = styled.div`
  margin: 4px 0 0 14px;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.5;
`;

// ─── Expandable Item ────────────────────────────────────────────────

function ExpandableItem({ item, color, showDescription = true }) {
  const [expanded, setExpanded] = useState(false);
  const hasDescription = showDescription && !!item.description;

  return (
    <BulletItem>
      <BulletRow
        $expandable={hasDescription}
        onClick={() => hasDescription && setExpanded(!expanded)}
      >
        <Dot $color={color} />
        <BulletTitle>{item.title}</BulletTitle>
        {hasDescription && (
          <ExpandIcon>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </ExpandIcon>
        )}
      </BulletRow>
      {expanded && item.description && (
        <BulletDescription>{item.description}</BulletDescription>
      )}
    </BulletItem>
  );
}

// ─── Main Component ─────────────────────────────────────────────────

/**
 * PivotSummaryView — Shows all 6 lenses with their obstacles and opportunities
 * for a single pivot point. Vertical card stack, iPad-friendly.
 */
export default function PivotSummaryView({
  pivotPoint,
  instanceId = null,
  pivotPointId = null,
  lenses = [],
  items = [],
  summaryText = '',
  lensCounts = {},
  loading = false,
  error = null,
  clusterData = null,
  clusterLoading = false,
  clusterError = null,
  pivotReport = null,
  reportLoading = false,
  onGenerateReport,
  onBack,
  onItemClick,
  showDescription = true,
}) {
  const { t, language } = useTranslation();
  const [activeView, setActiveView] = useState('clusters');
  const [localClusterData, setLocalClusterData] = useState(null);
  const [localClusterLoading, setLocalClusterLoading] = useState(false);
  const [localClusterError, setLocalClusterError] = useState(null);

  const loadLocalClusters = React.useCallback(async (forceRefresh = false) => {
    if (!instanceId || !pivotPointId) return;
    setLocalClusterLoading(true);
    setLocalClusterError(null);
    try {
      const title = pivotPoint?.title || '';
      const baseUrl = API_ENDPOINTS.LENS_PIVOT_CLUSTERS(instanceId, pivotPointId);
      const withTitle = title ? `${baseUrl}?title=${encodeURIComponent(title)}` : baseUrl;
      const url = forceRefresh ? `${withTitle}${withTitle.includes('?') ? '&' : '?'}refresh=1` : withTitle;
      const response = await fetch(url);
      if (!response.ok) throw new Error(t('lenses.clusterFetchFailed', { status: response.status }));
      setLocalClusterData(await response.json());
    } catch (err) {
      setLocalClusterError(err.message || t('lenses.clustersFetchFailed'));
      setLocalClusterData(null);
    } finally {
      setLocalClusterLoading(false);
    }
  }, [instanceId, pivotPointId, pivotPoint?.title, t]);

  React.useEffect(() => {
    if (clusterData || !instanceId || !pivotPointId) return;
    loadLocalClusters(false);
  }, [clusterData, instanceId, pivotPointId, loadLocalClusters]);

  // Group items by lens
  const itemsByLens = {};
  items.forEach(item => {
    if (!itemsByLens[item.lens_id]) itemsByLens[item.lens_id] = [];
    itemsByLens[item.lens_id].push(item);
  });

  // Totals
  const totalObstacles = items.filter(i => i.type === 'obstacle').length;
  const totalOpportunities = items.filter(i => i.type === 'opportunity').length;

  // Sort lenses by sort_order
  const sortedLenses = [...lenses].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const computedLensCounts = React.useMemo(() => {
    const counts = {};
    items.forEach((item) => {
      if (!counts[item.lens_id]) counts[item.lens_id] = { obstacles: 0, opportunities: 0 };
      if (item.type === 'obstacle') counts[item.lens_id].obstacles += 1;
      if (item.type === 'opportunity') counts[item.lens_id].opportunities += 1;
    });
    return counts;
  }, [items]);

  const effectiveLensCounts = React.useMemo(() => {
    const hasServerCounts = Object.values(lensCounts).some(
      (c) => (c.obstacles || 0) > 0 || (c.opportunities || 0) > 0
    );
    return hasServerCounts ? lensCounts : computedLensCounts;
  }, [lensCounts, computedLensCounts]);

  const summaryParagraphs = React.useMemo(() => {
    const cleaned = (summaryText || '').trim();
    if (!cleaned) return [];

    const byNewlines = cleaned
      .split(/\n{2,}/)
      .map(p => p.trim())
      .filter(Boolean);

    if (byNewlines.length >= 2) return byNewlines.slice(0, 3);

    // Fallback: split long single paragraph into roughly three chunks.
    const sentences = cleaned.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [cleaned];
    const targetSize = Math.max(1, Math.ceil(sentences.length / 3));
    const chunks = [];
    for (let i = 0; i < sentences.length; i += targetSize) {
      chunks.push(sentences.slice(i, i + targetSize).join(' ').trim());
    }
    return chunks.slice(0, 3);
  }, [summaryText]);

  const effectiveClusterData = clusterData || localClusterData;
  const effectiveClusterLoading = clusterLoading || localClusterLoading;
  const effectiveClusterError = clusterError || localClusterError;
  const hasAnyClusters =
    ((effectiveClusterData?.obstacleClusters?.length || 0) + (effectiveClusterData?.opportunityClusters?.length || 0)) > 0;
  const showClusters = activeView === 'clusters' && (hasAnyClusters || effectiveClusterLoading || !!effectiveClusterError);

  return (
    <Container>
      <HeaderBar>
        {onBack && (
          <BackButton onClick={onBack}>
            <ArrowLeft size={16} />
            {t('common.back')}
          </BackButton>
        )}
        <HeaderText>
          <PivotTitle>{pivotPoint?.title || t('lenses.pivotLabel')}</PivotTitle>
          {pivotPoint?.description && (
            <PivotDescription>{pivotPoint.description}</PivotDescription>
          )}
        </HeaderText>
      </HeaderBar>

      <SummaryBar>
        <SummaryBadge $color={COLORS.obstacle}>
          <AlertTriangle size={14} />
          {totalObstacles}{' '}
          {totalObstacles === 1 ? t('lenses.obstacleOne') : t('lenses.obstacleOther')}
        </SummaryBadge>
        <SummaryBadge $color={COLORS.opportunity}>
          <Lightbulb size={14} />
          {totalOpportunities}{' '}
          {totalOpportunities === 1 ? t('lenses.opportunityOne') : t('lenses.opportunityOther')}
        </SummaryBadge>
      </SummaryBar>

      <ScrollArea>
        <ContentGrid>
          <LeftColumn>
            <MetaSection>
              {loading && (
                <StatusMessage>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  {t('lenses.formingPivotSummary')}
                </StatusMessage>
              )}
              {!!error && (
                <StatusMessage style={{ borderColor: '#E94B6E55', color: '#ffb8c4' }}>
                  {error}
                </StatusMessage>
              )}
              {!loading && !error && summaryParagraphs.length > 0 && (
                <SummaryPanel>
                  <SummaryTitle>{t('lenses.tabBrief')}</SummaryTitle>
                  {summaryParagraphs.map((paragraph, idx) => (
                    <SummaryParagraph key={idx}>{paragraph}</SummaryParagraph>
                  ))}
                </SummaryPanel>
              )}

              {!loading && !error && (
                <SummaryPanel>
                  <SummaryTitle>{t('lenses.lensCountsTitle')}</SummaryTitle>
                  <LensCountsGrid>
                    {sortedLenses.map(lens => {
                      const counts = effectiveLensCounts[lens.id] || { obstacles: 0, opportunities: 0 };
                      return (
                        <LensCountCard key={lens.id}>
                          <LensCountTitle>{lensDisplayName(lens, language)}</LensCountTitle>
                          <LensCountRow>
                            <span style={{ color: COLORS.obstacle }}>
                              {t('lenses.obstaclesCountShort', { count: counts.obstacles || 0 })}
                            </span>
                            <span style={{ color: COLORS.opportunity }}>
                              {t('lenses.opportunitiesCountShort', { count: counts.opportunities || 0 })}
                            </span>
                          </LensCountRow>
                        </LensCountCard>
                      );
                    })}
                  </LensCountsGrid>
                </SummaryPanel>
              )}
            </MetaSection>

            <ViewToggleRow>
              <ToggleGroup>
                <ToggleBtn type="button" $active={activeView === 'clusters'} onClick={() => setActiveView('clusters')}>
                  {t('lenses.viewClusters')}
                </ToggleBtn>
                <ToggleBtn type="button" $active={activeView === 'lenses'} onClick={() => setActiveView('lenses')}>
                  {t('lenses.viewLenses')}
                </ToggleBtn>
              </ToggleGroup>
              {effectiveClusterData?.stale && (
                <StaleClusterHint>{t('lenses.clustersStaleHint')}</StaleClusterHint>
              )}
            </ViewToggleRow>

            {activeView === 'clusters' && effectiveClusterLoading && (
              <StatusMessage style={{ margin: '0 auto 12px', maxWidth: 720 }}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                {t('lenses.formingClusters')}
              </StatusMessage>
            )}
            {activeView === 'clusters' && !!effectiveClusterError && (
              <StatusMessage style={{ margin: '0 auto 12px', maxWidth: 720, borderColor: '#E94B6E55', color: '#ffb8c4' }}>
                {effectiveClusterError}
              </StatusMessage>
            )}

            {showClusters ? (
              <div style={{ maxWidth: 720, margin: '0 auto' }}>
                <PivotClusteredListView
                  obstacleClusters={effectiveClusterData?.obstacleClusters || []}
                  opportunityClusters={effectiveClusterData?.opportunityClusters || []}
                  items={effectiveClusterData?.items || items}
                  showDescription={showDescription}
                  onItemClick={onItemClick}
                />
              </div>
            ) : (
              <CardsContainer>
                {sortedLenses.map(lens => {
                  const lensItems = itemsByLens[lens.id] || [];
                  const obstacles = lensItems.filter(i => i.type === 'obstacle');
                  const opportunities = lensItems.filter(i => i.type === 'opportunity');
                  const isEmpty = lensItems.length === 0;

                  return (
                    <LensCardWrapper key={lens.id} $color={lens.color}>
                      <LensCardHeader>
                        <LensImage $imageUrl={lens.image_url} $color={lens.color || '#367ABA'} />
                        <LensName>{lensDisplayName(lens, language)}</LensName>
                        {obstacles.length > 0 && (
                          <LensCountBadge $color={COLORS.obstacle}>
                            <AlertTriangle size={12} />
                            {obstacles.length}
                          </LensCountBadge>
                        )}
                        {opportunities.length > 0 && (
                          <LensCountBadge $color={COLORS.opportunity}>
                            <Lightbulb size={12} />
                            {opportunities.length}
                          </LensCountBadge>
                        )}
                      </LensCardHeader>

                      <LensCardBody>
                        {isEmpty ? (
                          <EmptyLens>{t('lenses.noItems')}</EmptyLens>
                        ) : (
                          <>
                            {obstacles.length > 0 && (
                              <TypeSection>
                                <TypeLabel $color={COLORS.obstacle}>
                                  <AlertTriangle size={12} />
                                  {t('lenses.obstacles')}
                                </TypeLabel>
                                <BulletList>
                                  {obstacles.map(item => (
                                    <ExpandableItem key={item.id} item={item} color={COLORS.obstacle} showDescription={showDescription} />
                                  ))}
                                </BulletList>
                              </TypeSection>
                            )}
                            {opportunities.length > 0 && (
                              <TypeSection>
                                <TypeLabel $color={COLORS.opportunity}>
                                  <Lightbulb size={12} />
                                  {t('lenses.opportunities')}
                                </TypeLabel>
                                <BulletList>
                                  {opportunities.map(item => (
                                    <ExpandableItem key={item.id} item={item} color={COLORS.opportunity} showDescription={showDescription} />
                                  ))}
                                </BulletList>
                              </TypeSection>
                            )}
                          </>
                        )}
                      </LensCardBody>
                    </LensCardWrapper>
                  );
                })}
              </CardsContainer>
            )}
          </LeftColumn>

          <RightSidebar>
            <SummaryPanel>
              <SummaryTitle>{t('lenses.pivotAnalysis')}</SummaryTitle>
              <SummaryParagraph style={{ marginBottom: 6 }}>
                <Users size={12} style={{ marginRight: 6 }} />
                {t('lenses.membersCount', {
                  count: pivotPoint?.memberCount || pivotPoint?.memberIds?.length || 0,
                })}
              </SummaryParagraph>
              {(pivotPoint?.challengeSeverity || pivotPoint?.solutionOptimism) && (
                <SummaryParagraph style={{ marginBottom: 0 }}>
                  {t('lenses.severityOptimism', {
                    severity: pivotPoint?.challengeSeverity || '-',
                    optimism: pivotPoint?.solutionOptimism || '-',
                  })}
                </SummaryParagraph>
              )}
            </SummaryPanel>

            <SummaryPanel>
              <SummaryTitle>
                <FileText size={13} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
                {t('lenses.report')}
              </SummaryTitle>

              {pivotReport?.content ? (
                // The report is AI-written HTML built from participant text
                // and RAG chunks — sanitize before injecting (#935).
                <ReportContent dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pivotReport.content) }} />
              ) : (
                <>
                  <SummaryParagraph style={{ marginBottom: 12 }}>
                    {t('lenses.reportNotCreated')}
                  </SummaryParagraph>
                  {onGenerateReport && (
                    <ReportButton onClick={onGenerateReport} disabled={reportLoading}>
                      {reportLoading ? (
                        <>
                          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          {t('lenses.creating')}
                        </>
                      ) : (
                        <>
                          <Sparkles size={14} />
                          {t('lenses.createReport')}
                        </>
                      )}
                    </ReportButton>
                  )}
                </>
              )}

              {pivotReport?.content && onGenerateReport && (
                <ReportButton onClick={onGenerateReport} disabled={reportLoading} style={{ marginTop: 10 }}>
                  {reportLoading ? (
                    <>
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      {t('lenses.recreating')}
                    </>
                  ) : (
                    <>
                      <RefreshCw size={14} />
                      {t('lenses.recreate')}
                    </>
                  )}
                </ReportButton>
              )}
            </SummaryPanel>
          </RightSidebar>
        </ContentGrid>
      </ScrollArea>
    </Container>
  );
}
